/* eslint-disable no-empty */
/* eslint-disable max-len */
/* eslint-disable import/no-extraneous-dependencies */
import React, {
  useContext, useState, useRef, useMemo, useEffect, useLayoutEffect, useCallback,
} from 'react';
import { createPortal } from 'react-dom';
import { useHistory, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import {
  X, ChevronLeft, ChevronRight, Check,
} from 'lucide-react';
import { UserContext } from '../../contexts/User';
import {
  getAccessibleSteps, TOUR_START_EVENT, TOUR_CHECK_EVENT, WELCOME_TOUR_ID,
} from './steps';
import { getTourStatus, setTourStatus } from './tourApi';

const SPOTLIGHT_PAD = 8; // breathing room around the highlighted element
const CARD_W = 340; // keep in sync with the card's width class below
const CARD_GAP = 14; // distance between the card and its anchor
const MARGIN = 16; // min distance from any viewport edge

const rectOf = (el) => {
  const r = el.getBoundingClientRect();
  return {
    top: r.top, left: r.left, width: r.width, height: r.height,
  };
};

const clamp = (v, min, max) => Math.max(min, Math.min(v, max));

// Place the popover beside the target per the step's preferred side, flipping to
// the opposite side when it would overflow, then clamping inside the viewport.
// With no target (welcome / fallback) the card is centred on screen.
function computeCardPosition(rect, placement, cw, ch) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  if (!rect || placement === 'center') {
    return { top: (vh - ch) / 2, left: (vw - cw) / 2, centered: true };
  }

  let top;
  let left;
  switch (placement) {
    case 'top': top = rect.top - ch - CARD_GAP; left = rect.left; break;
    case 'left': left = rect.left - cw - CARD_GAP; top = rect.top; break;
    case 'right': left = rect.left + rect.width + CARD_GAP; top = rect.top; break;
    case 'bottom':
    default: top = rect.top + rect.height + CARD_GAP; left = rect.left; break;
  }

  // Flip to the opposite side if the preferred side overflows.
  if (placement === 'bottom' && top + ch > vh - MARGIN) top = rect.top - ch - CARD_GAP;
  if (placement === 'top' && top < MARGIN) top = rect.top + rect.height + CARD_GAP;
  if (placement === 'right' && left + cw > vw - MARGIN) left = rect.left - cw - CARD_GAP;
  if (placement === 'left' && left < MARGIN) left = rect.left + rect.width + CARD_GAP;

  return {
    top: clamp(top, MARGIN, Math.max(MARGIN, vh - ch - MARGIN)),
    left: clamp(left, MARGIN, Math.max(MARGIN, vw - cw - MARGIN)),
    centered: false,
  };
}

function GuidedTour() {
  const history = useHistory();
  const location = useLocation();
  const { member, activeWorkspace } = useContext(UserContext);

  const [active, setActive] = useState(false);
  const [activeTour, setActiveTour] = useState(WELCOME_TOUR_ID);
  const [index, setIndex] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const [cardStyle, setCardStyle] = useState(null);

  const steps = useMemo(() => getAccessibleSteps(member, activeTour), [member, activeTour]);

  const targetElRef = useRef(null);
  const cardRef = useRef(null);
  // Latest member/workspace/track, read inside async handlers and on close
  // without re-subscribing their effects.
  const memberRef = useRef(member);
  const workspaceRef = useRef(activeWorkspace);
  const activeTourRef = useRef(activeTour);
  const nextRef = useRef(null);
  memberRef.current = member;
  workspaceRef.current = activeWorkspace;
  activeTourRef.current = activeTour;

  const step = active ? steps[index] : null;

  // ── controls ─────────────────────────────────────────────────────────────
  const start = useCallback((tourId) => {
    setActiveTour(tourId || WELCOME_TOUR_ID);
    setIndex(0);
    setActive(true);
  }, []);

  const close = useCallback((status) => {
    setActive(false);
    setTargetRect(null);
    targetElRef.current = null;
    const wsId = workspaceRef.current?.id;
    if (wsId) setTourStatus(wsId, status, activeTourRef.current).catch(() => {});
  }, []);

  const next = useCallback(() => {
    if (index >= steps.length - 1) close('completed');
    else setIndex(index + 1);
  }, [index, steps.length, close]);
  nextRef.current = next;

  const prev = useCallback(() => {
    setIndex((i) => Math.max(0, i - 1));
  }, []);

  // Start only if this member hasn't completed/dismissed the tour yet. Backed by
  // the member's tourStatus, so it follows them across browsers and devices.
  const startIfPending = useCallback(async (workspaceId) => {
    if (active) return;
    const wsId = workspaceId || workspaceRef.current?.id;
    if (!wsId) return;
    if (getAccessibleSteps(memberRef.current).length < 2) return; // not eligible
    try {
      const status = await getTourStatus(wsId);
      if (status === 'pending') start();
    } catch (e) {}
  }, [active, start]);

  // ── launch triggers
  //  - shoot:start-tour  → force start (Help center "Take the tour")
  //  - shoot:tour-check  → start only if pending (fired on workspace selection)
  useEffect(() => {
    const onStart = (e) => start(e?.detail?.tour);
    const onCheck = (e) => startIfPending(e?.detail?.workspaceId);
    window.addEventListener(TOUR_START_EVENT, onStart);
    window.addEventListener(TOUR_CHECK_EVENT, onCheck);
    return () => {
      window.removeEventListener(TOUR_START_EVENT, onStart);
      window.removeEventListener(TOUR_CHECK_EVENT, onCheck);
    };
  }, [start, startIfPending]);

  // ── navigate to the step's route
  useEffect(() => {
    if (!active || !step) return;
    if (location.pathname !== step.route) history.push(step.route);
    // location.pathname intentionally omitted: only react to step changes here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, index]);

  // ── find & track the anchor element for the current step
  useEffect(() => {
    if (!active || !step) return undefined;
    setTargetRect(null);
    targetElRef.current = null;
    if (!step.selector) return undefined; // centred step, no anchor

    let cancelled = false;
    let timer;
    let measureTimer;
    let tries = 0;
    const tick = () => {
      if (cancelled) return;
      if (location.pathname === step.route) {
        const el = document.querySelector(step.selector);
        if (el) {
          targetElRef.current = el;
          try { el.scrollIntoView({ block: 'center', inline: 'nearest' }); } catch (e) {}
          // let the scroll settle before measuring the final position
          measureTimer = window.setTimeout(() => {
            if (!cancelled) setTargetRect(rectOf(el));
          }, 220);
          return;
        }
      }
      tries += 1;
      // Optional steps anchor conditionally-rendered UI; if it never shows, skip
      // on after ~1.5s rather than parking on a centred fallback. Required steps
      // keep the ~5s budget, then degrade to a centred card.
      const budget = step.optional ? 18 : 60;
      if (tries > budget) {
        if (step.optional && nextRef.current) nextRef.current();
        return;
      }
      timer = window.setTimeout(tick, 80);
    };
    tick();
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      window.clearTimeout(measureTimer);
    };
  }, [active, index, location.pathname, step]);

  // ── keep the highlight aligned as the page scrolls or resizes
  useEffect(() => {
    if (!active) return undefined;
    let raf = 0;
    const onMove = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        if (targetElRef.current) setTargetRect(rectOf(targetElRef.current));
      });
    };
    window.addEventListener('resize', onMove);
    window.addEventListener('scroll', onMove, true);
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener('resize', onMove);
      window.removeEventListener('scroll', onMove, true);
    };
  }, [active]);

  // ── position the card relative to the (possibly missing) anchor
  useLayoutEffect(() => {
    if (!active || !step) return;
    const el = cardRef.current;
    const cw = el ? el.offsetWidth : CARD_W;
    const ch = el ? el.offsetHeight : 190;
    const pos = computeCardPosition(targetRect, step.placement, cw, ch);
    setCardStyle((prev) => (prev
      && Math.abs(prev.top - pos.top) < 0.5
      && Math.abs(prev.left - pos.left) < 0.5
      && prev.centered === pos.centered ? prev : pos));
  }, [active, index, targetRect, step]);

  // ── keyboard navigation
  useEffect(() => {
    if (!active) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close('dismissed');
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        next();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        prev();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, next, prev, close]);

  // ── move focus to the card on each step for screen-reader / keyboard users
  useEffect(() => {
    if (active && cardRef.current) cardRef.current.focus();
  }, [active, index]);

  if (!active || !step || !cardStyle) return null;

  const total = steps.length;
  const isLast = index === total - 1;
  const spotlight = step.selector && targetRect ? targetRect : null;

  return createPortal(
    <div className="fixed inset-0" style={{ zIndex: 99998, fontFamily: 'Inter, sans-serif' }} aria-hidden={false}>
      {/* Click-blocking backdrop. Dimmed directly only on centred steps; on
          anchored steps the dimming comes from the spotlight's box-shadow. */}
      <div
        className="absolute inset-0"
        style={{ background: spotlight ? 'transparent' : 'rgba(14,30,64,0.55)', pointerEvents: 'auto' }}
      />

      {/* Spotlight cut-out over the anchored element */}
      {spotlight && (
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: spotlight.top - SPOTLIGHT_PAD,
            left: spotlight.left - SPOTLIGHT_PAD,
            width: spotlight.width + SPOTLIGHT_PAD * 2,
            height: spotlight.height + SPOTLIGHT_PAD * 2,
            borderRadius: 12,
            boxShadow: '0 0 0 2px rgba(255,255,255,0.95), 0 0 0 9999px rgba(14,30,64,0.55)',
            transition: 'top 0.25s ease, left 0.25s ease, width 0.25s ease, height 0.25s ease',
            pointerEvents: 'none',
          }}
        />
      )}

      {/* Popover card */}
      <AnimatePresence mode="wait">
        <motion.div
          key={step.id}
          ref={cardRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="shoot-tour-title"
          tabIndex={-1}
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.96 }}
          transition={{ duration: 0.18, ease: 'easeOut' }}
          className="rounded-xl bg-white shadow-[0_12px_32px_rgba(14,30,64,0.22)] border border-surface-2 outline-none"
          style={{
            position: 'absolute',
            top: cardStyle.top,
            left: cardStyle.left,
            width: CARD_W,
            maxWidth: 'calc(100vw - 32px)',
            pointerEvents: 'auto',
          }}
        >
          <div className="p-5">
            <div className="flex items-center justify-between mb-2.5">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-brand-500" style={{ letterSpacing: '0.06em' }}>
                Step
                {' '}
                {index + 1}
                {' '}
                of
                {' '}
                {total}
              </span>
              <button
                type="button"
                onClick={() => close('dismissed')}
                aria-label="Close tour"
                className="p-1 -mr-1 -mt-1 rounded text-ink-500 hover:bg-surface-page transition"
              >
                <X size={16} />
              </button>
            </div>

            <h3 id="shoot-tour-title" className="text-lg font-bold text-ink-900 m-0 leading-tight">
              {step.title}
            </h3>
            <p className="text-sm text-ink-700 leading-6 mt-1.5 mb-0">
              {step.body}
            </p>

            {/* Progress dots */}
            <div className="flex items-center gap-1.5 mt-4">
              {steps.map((s, i) => (
                <span
                  key={s.id}
                  className="h-1.5 rounded-full transition-all"
                  style={{
                    width: i === index ? 18 : 6,
                    background: i === index ? '#213d69' : '#d7dbe4',
                  }}
                />
              ))}
            </div>

            <div className="flex items-center justify-between mt-4">
              <button
                type="button"
                onClick={() => close('dismissed')}
                className="text-sm font-medium text-ink-500 hover:text-ink-700 transition"
              >
                Skip tour
              </button>
              <div className="flex items-center gap-2">
                {index > 0 && (
                  <button
                    type="button"
                    onClick={prev}
                    className="inline-flex items-center gap-1 px-3 py-2 rounded-[8px] text-sm font-semibold text-ink-900 border border-rule bg-white hover:bg-surface-page transition"
                    style={{ borderStyle: 'solid' }}
                  >
                    <ChevronLeft size={15} />
                    Back
                  </button>
                )}
                <button
                  type="button"
                  onClick={next}
                  className="inline-flex items-center gap-1 px-3.5 py-2 rounded-[8px] text-sm font-semibold text-white bg-brand-700 hover:bg-brand-500 transition"
                >
                  {isLast ? 'Finish' : 'Next'}
                  {isLast ? <Check size={15} /> : <ChevronRight size={15} />}
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>,
    document.body,
  );
}

export default GuidedTour;
